import type { ProtocolTimelineProps } from '@/components/admin/protocoles/contracts';
import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { JumpLane } from '@/components/admin/protocoles/timeline/jump-lane';
import { JumpPopover } from '@/components/admin/protocoles/timeline/jump-popover';
import { ProtocolTree } from '@/components/admin/protocoles/timeline/protocol-tree';
import { StepCard, stepCardName } from '@/components/admin/protocoles/timeline/step-card';
import {
  countLabel,
  jumpLabel,
  objectiveLookupOf,
  stepLookupOf,
  stepTitle,
} from '@/lib/protocol-labels';
import type { LaneBox } from '@/lib/protocol-lanes';
import { jumpKey, laneLayout } from '@/lib/protocol-lanes';
import { protocolJumps, protocolProblems } from '@matter_extractor/shared';
import {
  CircleX,
  ListTree,
  Plus,
  Repeat,
  Route,
  TriangleAlert,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import type { DragEvent, MouseEvent, PointerEvent } from 'react';
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * The FRISE and the TREE of a protocol (refonte modulaire §7.1; maquette
 * `editeur-protocole.html`, écran 3).
 *
 * `+ - [Étape 1] - + - [Étape 2] - + ...`, the jumps of the selected step drawn
 * in lanes above and below it, and - behind the same toolbar - the read-only
 * tree that unrolls the whole protocol. Two views of one thing, which is why
 * they share a component and a segmented control rather than sitting in two
 * places on the page.
 *
 * ## Controlled, with the LOOK held here
 *
 * The steps and the selection come from the page (`ProtocolTimelineProps`) and
 * every change leaves through a callback. What stays inside is what nothing
 * else can read: which view is up, the zoom, the three switches, and the drag
 * in flight. None of them belongs in the draft - an operator who zooms has not
 * modified the protocol - and none of them survives leaving the screen, which
 * is what the mockup's own `S` object says too.
 *
 * ## Where the lanes get their coordinates
 *
 * From the DOM, and from nowhere else: the cards are laid out by flexbox, so
 * only the browser knows where they end up. A `useLayoutEffect` reads
 * `offsetLeft` / `offsetWidth` / `offsetTop` / `offsetHeight` off each card
 * before the paint, a `ResizeObserver` re-reads them when the track's own size
 * moves, and the numbers go to `lib/protocol-lanes.ts`, which turns them into
 * paths WITHOUT touching the DOM. That is the whole reason the geometry is a
 * module: jsdom has no layout, so the paths could not be asserted anywhere else.
 *
 * The effect is a MEASUREMENT and not a data fetch - the same distinction
 * `versioned-editor.tsx` draws for its cleanup effect. The repo's ban is on
 * `useEffect` + fetch as a way of LOADING data; this one loads nothing, it
 * reads back what the render just produced.
 *
 * ## The two things the frise refuses to guess
 *
 * A jump aims at a step's id and never at its rank (§7.1: « un déplacement ne
 * casse rien »), so a drag-and-drop only ever calls `onMove` - the corridors
 * follow because they were never drawn from positions. And a jump whose target
 * no longer exists is NOT drawn: `protocolJumps` drops it, and the yellow
 * triangle on the card is what says so, because an arrow into nowhere would
 * hide a refusal behind a shape.
 */

/** 50 to 125 % (§7.1). Four steps, so the two buttons are enough. */
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25] as const;
type ZoomLevel = (typeof ZOOM_LEVELS)[number];

/** The track's own size and where each card sits inside it. */
type TrackGeometry = {
  readonly width: number;
  readonly height: number;
  readonly boxes: Readonly<Record<string, LaneBox>>;
};

const EMPTY_GEOMETRY: TrackGeometry = { width: 0, height: 0, boxes: {} };

/**
 * Whether a fresh measurement says anything new.
 *
 * The measuring effect runs after EVERY render - it is the only way to catch a
 * card that moved for a reason this component cannot enumerate (a name grew, a
 * badge appeared, the window changed) - so the state it writes has to settle,
 * or the effect would schedule the render that runs the effect.
 */
function sameGeometry(one: TrackGeometry, other: TrackGeometry): boolean {
  if (one.width !== other.width || one.height !== other.height) return false;
  const ids = Object.keys(other.boxes);
  if (Object.keys(one.boxes).length !== ids.length) return false;
  return ids.every((id) => {
    const a = one.boxes[id];
    const b = other.boxes[id];
    return (
      a !== undefined &&
      b !== undefined &&
      a.cx === b.cx &&
      a.top === b.top &&
      a.bottom === b.bottom
    );
  });
}

/**
 * The one-pixel rule between a « + » and the card beside it.
 *
 * `bg-rail` and not a grey utility, for the reason the token was declared: the
 * charte forbids gray-200 and darker as the GROUND of a surface, and
 * `design-system-adherence.test.ts` refuses the bare utility outright. A
 * hairline that joins two controls is neither a surface nor a border - it is
 * the line the frise is read along, which is what the persona slider's track is
 * too.
 */
function Connector() {
  return <span aria-hidden="true" className="h-px w-3.5 flex-none bg-rail" />;
}

const SEGMENT_BASE =
  'inline-flex h-8.5 cursor-pointer items-center gap-1.5 px-3 font-semibold text-sm';
const ICON_BUTTON =
  'inline-grid size-7.5 cursor-pointer place-items-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-noir disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent';

export function ProtocolTimeline({
  steps,
  selectedStepId,
  readOnly,
  onSelect,
  onInsert,
  onMove,
  onRemoveEmpty,
  visible = true,
}: ProtocolTimelineProps) {
  const [view, setView] = useState<'frise' | 'tree'>('frise');
  const [zoom, setZoom] = useState<ZoomLevel>(1);
  const [links, setLinks] = useState(true);
  const [labels, setLabels] = useState(true);
  const [follow, setFollow] = useState(true);
  const [dragged, setDragged] = useState<string | null>(null);
  const [dropSlot, setDropSlot] = useState<number | null>(null);
  const [pointed, setPointed] = useState<string | null>(null);
  const [geometry, setGeometry] = useState<TrackGeometry>(EMPTY_GEOMETRY);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const cardNodes = useRef(new Map<string, HTMLElement>());
  const pan = useRef<{ x: number; left: number; moved: boolean } | null>(null);
  const panned = useRef(false);

  const registerNode = useCallback((stepId: string, node: HTMLElement | null): void => {
    if (node === null) cardNodes.current.delete(stepId);
    else cardNodes.current.set(stepId, node);
  }, []);

  const measure = useCallback((): void => {
    const track = trackRef.current;
    if (track === null) return;
    const boxes: Record<string, LaneBox> = {};
    for (const [stepId, node] of cardNodes.current) {
      boxes[stepId] = {
        cx: node.offsetLeft + node.offsetWidth / 2,
        top: node.offsetTop,
        bottom: node.offsetTop + node.offsetHeight,
      };
    }
    const next: TrackGeometry = {
      width: track.offsetWidth,
      height: track.offsetHeight,
      boxes,
    };
    setGeometry((current) => (sameGeometry(current, next) ? current : next));
  }, []);

  /*
    No dependency list, deliberately. What moves a card is not a list this
    component can keep honest - a longer name, a badge that appears, a step
    inserted, the zoom, the view - and `measure` settles on its own, so running
    it after every render costs one layout read and gets the corridors right on
    the frame the cards actually moved.
  */
  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    const track = trackRef.current;
    /* jsdom implements no `ResizeObserver`, and the suites need none: they
       assert the corridors exist, `protocol-lanes.test.ts` asserts where. */
    if (track === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(track);
    return () => observer.disconnect();
  }, [measure]);

  /*
    §7.1 wants the selected step in the middle of the banner after a selection
    or an insertion - the card an operator just asked for is the one they must
    not have to look for. `scrollTo` is missing in jsdom, hence the guard.

    Not while the frise is hidden (review of !474): its track measures zero
    there, so the scroll would land nowhere - and `visible` is a dependency, so
    a selection made meanwhile is centred when the frise shows again.
  */
  useEffect(() => {
    if (!visible) return;
    const scroller = scrollerRef.current;
    const card = cardNodes.current.get(selectedStepId);
    if (scroller === null || card === undefined) return;
    if (typeof scroller.scrollTo !== 'function') return;
    scroller.scrollTo({
      left: (card.offsetLeft + card.offsetWidth / 2) * zoom - scroller.clientWidth / 2,
      behavior: 'smooth',
    });
  }, [selectedStepId, zoom, visible]);

  const jumps = useMemo(() => protocolJumps(steps), [steps]);
  const stepAt = useMemo(() => stepLookupOf(steps), [steps]);
  const objectiveAt = useMemo(() => objectiveLookupOf(steps), [steps]);

  /*
    `stepProblems(steps, id)` per card would walk the whole protocol once per
    step; the rule is the same one, counted in a single pass.
  */
  const problems = useMemo(() => {
    const counts = new Map<string, number>();
    for (const problem of protocolProblems(steps)) {
      counts.set(problem.stepId, (counts.get(problem.stepId) ?? 0) + 1);
    }
    return counts;
  }, [steps]);

  const lanes = useMemo(
    () =>
      laneLayout({
        order: steps.map((step) => step.id),
        boxes: geometry.boxes,
        selectedId: selectedStepId,
        jumps,
      }),
    [steps, geometry.boxes, selectedStepId, jumps],
  );

  const pointedLane = useMemo(() => {
    if (pointed === null) return null;
    const leaving = lanes.outgoing.find((lane) => jumpKey(lane.jump) === pointed);
    if (leaving !== undefined) return { lane: leaving, incoming: false };
    const arriving = lanes.incoming.find((lane) => jumpKey(lane.jump) === pointed);
    return arriving === undefined ? null : { lane: arriving, incoming: true };
  }, [pointed, lanes]);

  const zoomIndex = ZOOM_LEVELS.indexOf(zoom);
  const selectedPlace = stepAt(selectedStepId);

  function changeZoom(by: -1 | 1): void {
    const next = ZOOM_LEVELS[zoomIndex + by];
    if (next !== undefined) setZoom(next);
  }

  function onSlotDragOver(index: number, event: DragEvent<HTMLButtonElement>): void {
    if (dragged === null) return;
    // Without this the browser refuses the drop and no `drop` event fires.
    event.preventDefault();
    setDropSlot(index);
  }

  function onSlotDrop(index: number): void {
    const from = steps.findIndex((step) => step.id === dragged);
    setDragged(null);
    setDropSlot(null);
    if (from < 0) return;
    /* The two slots on either side of the dragged card are where it already
       is: a drop there is not a move, and calling `onMove` would mark the
       draft dirty for a gesture that changed nothing. */
    if (index === from || index === from + 1) return;
    onMove(from, index > from ? index - 1 : index);
  }

  function openJump(target: string): void {
    if (!follow) return;
    onSelect(target);
  }

  function onTrackPointerDown(event: PointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    /* Grabbing the BACKGROUND pans; grabbing a card, a « + » or a corridor is
       that control's own gesture (§7.1: « en saisissant son fond »). */
    if (target.closest('button, [role="button"]') !== null) return;
    const scroller = scrollerRef.current;
    if (scroller === null) return;
    pan.current = { x: event.clientX, left: scroller.scrollLeft, moved: false };
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  }

  function onTrackPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const current = pan.current;
    const scroller = scrollerRef.current;
    if (current === null || scroller === null) return;
    const dx = event.clientX - current.x;
    if (Math.abs(dx) > 3) current.moved = true;
    scroller.scrollLeft = current.left - dx;
  }

  function endPan(): void {
    const current = pan.current;
    if (current === null) return;
    pan.current = null;
    /* A pan that MOVED ends on a click the browser fires anyway - swallowed
       below, or the card the pointer happened to land on would be selected. */
    panned.current = current.moved;
  }

  function onTrackClickCapture(event: MouseEvent<HTMLDivElement>): void {
    if (!panned.current) return;
    panned.current = false;
    event.stopPropagation();
    event.preventDefault();
  }

  function slotLabel(index: number): string {
    const next = steps[index];
    return next === undefined
      ? 'Ajouter une étape à la fin'
      : `Insérer une étape avant ${stepCardName(next, index)}`;
  }

  function insertSlot(index: number) {
    return (
      <button
        type="button"
        data-testid={PROTOCOL_TEST_IDS.insertStep}
        data-slot={index}
        data-drop={dropSlot === index ? 'true' : undefined}
        aria-label={slotLabel(index)}
        onClick={() => onInsert(index)}
        onDragOver={(event) => onSlotDragOver(index, event)}
        onDragLeave={() => setDropSlot((current) => (current === index ? null : current))}
        onDrop={() => onSlotDrop(index)}
        className={`inline-grid size-6 flex-none cursor-pointer place-items-center rounded-full border transition-colors ${
          dropSlot === index
            ? 'border-bleu-nuit bg-bleu-nuit-tint text-bleu-nuit'
            : 'border-gray-300 bg-white text-gray-600 hover:border-bleu-nuit hover:text-bleu-nuit'
        }`}
      >
        <Plus aria-hidden="true" className="size-3.5" />
      </button>
    );
  }

  return (
    <section
      data-testid={PROTOCOL_TEST_IDS.timeline}
      className="rounded-md border-2 border-gray-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 text-gray-700 text-sm">
        <div className="flex flex-wrap items-center gap-3.5">
          <div
            // biome-ignore lint/a11y/useSemanticElements: a <fieldset> groups form controls under a <legend>; these are two toggle buttons named by their label.
            role="group"
            aria-label="Vue du protocole"
            className="inline-flex overflow-hidden rounded-md border-2 border-bleu-nuit"
          >
            <button
              type="button"
              data-testid={PROTOCOL_TEST_IDS.viewTimeline}
              aria-pressed={view === 'frise'}
              onClick={() => setView('frise')}
              className={`${SEGMENT_BASE} ${
                view === 'frise' ? 'bg-bleu-nuit text-white' : 'bg-white text-bleu-nuit'
              }`}
            >
              <Route aria-hidden="true" className="size-3.5" />
              frise
            </button>
            <button
              type="button"
              data-testid={PROTOCOL_TEST_IDS.viewTree}
              aria-pressed={view === 'tree'}
              onClick={() => setView('tree')}
              className={`${SEGMENT_BASE} border-bleu-nuit border-l-2 ${
                view === 'tree' ? 'bg-bleu-nuit text-white' : 'bg-white text-bleu-nuit'
              }`}
            >
              <ListTree aria-hidden="true" className="size-3.5" />
              arbre
            </button>
          </div>
          <p>
            {`${countLabel(steps.length, 'étape')} · ${countLabel(jumps.length, 'saut')}`}
            {selectedPlace === undefined ? null : (
              <>
                {' · étape '}
                <span className="font-semibold">{stepTitle(selectedPlace)}</span>
                {' sélectionnée'}
              </>
            )}
          </p>
        </div>
        {/* The zoom and the three switches belong to the frise: the tree has no
            corridors to hide and no scale to change. */}
        {view === 'frise' ? (
          <div className="flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-0.5">
              <button
                type="button"
                data-testid={PROTOCOL_TEST_IDS.zoomOut}
                aria-label="Réduire la frise"
                disabled={zoomIndex <= 0}
                onClick={() => changeZoom(-1)}
                className={ICON_BUTTON}
              >
                <ZoomOut aria-hidden="true" className="size-3.5" />
              </button>
              <span className="min-w-11 text-center tabular-nums">{`${Math.round(zoom * 100)} %`}</span>
              <button
                type="button"
                data-testid={PROTOCOL_TEST_IDS.zoomIn}
                aria-label="Agrandir la frise"
                disabled={zoomIndex >= ZOOM_LEVELS.length - 1}
                onClick={() => changeZoom(1)}
                className={ICON_BUTTON}
              >
                <ZoomIn aria-hidden="true" className="size-3.5" />
              </button>
            </span>
            <label className="inline-flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                data-testid={PROTOCOL_TEST_IDS.toggleLinks}
                checked={links}
                onChange={(event) => setLinks(event.target.checked)}
                className="size-4 accent-bleu-nuit"
              />
              liens
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                data-testid={PROTOCOL_TEST_IDS.toggleLabels}
                checked={labels}
                disabled={!links}
                onChange={(event) => setLabels(event.target.checked)}
                className="size-4 accent-bleu-nuit"
              />
              étiquettes
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                data-testid={PROTOCOL_TEST_IDS.toggleFollow}
                checked={follow}
                disabled={!links}
                onChange={(event) => setFollow(event.target.checked)}
                className="size-4 accent-bleu-nuit"
              />
              le clic sur un saut ouvre l’étape visée
            </label>
          </div>
        ) : null}
      </div>

      {view === 'tree' ? (
        <ProtocolTree steps={steps} selectedStepId={selectedStepId} onSelect={onSelect} />
      ) : (
        <>
          <div className="relative mt-2.5 rounded-md border border-gray-200 bg-gray-50">
            <div ref={scrollerRef} className="overflow-x-auto overflow-y-hidden rounded-md">
              {/*
                The wrapper carries the SCALED size so the scrollbar measures
                what the eye sees: a `transform` leaves layout untouched, so
                without it a frise at 125 % would scroll as if it were at 100 %.
              */}
              <div
                className="relative"
                style={
                  geometry.width > 0
                    ? { width: geometry.width * zoom, height: geometry.height * zoom }
                    : undefined
                }
              >
                <div
                  ref={trackRef}
                  onPointerDown={onTrackPointerDown}
                  onPointerMove={onTrackPointerMove}
                  onPointerUp={endPan}
                  onPointerCancel={endPan}
                  onClickCapture={onTrackClickCapture}
                  style={{ transform: `scale(${zoom})`, transformOrigin: 'left top' }}
                  /* py-24 is the room the corridors run in: fourteen pixels
                     clear of the cards, then fifteen per lane, plus the label
                     above the highest one. */
                  className="relative flex w-max cursor-grab items-center px-5 py-24 active:cursor-grabbing"
                >
                  <svg
                    role="presentation"
                    width={geometry.width}
                    height={geometry.height}
                    viewBox={`0 0 ${geometry.width} ${geometry.height}`}
                    className="pointer-events-none absolute top-0 left-0 overflow-visible"
                  >
                    {links
                      ? lanes.outgoing.map((lane) => {
                          const target = stepAt(lane.jump.to);
                          const label = jumpLabel(lane.jump, steps, objectiveAt);
                          return (
                            <JumpLane
                              key={jumpKey(lane.jump)}
                              lane={lane}
                              incoming={false}
                              showLabel={labels}
                              label={label}
                              name={`Saut vers ${target === undefined ? 'une étape disparue' : stepTitle(target)} · ${label}`}
                              onOpen={() => openJump(lane.jump.to)}
                              onPoint={() => setPointed(jumpKey(lane.jump))}
                              onLeave={() => setPointed(null)}
                            />
                          );
                        })
                      : null}
                    {links
                      ? lanes.incoming.map((lane) => {
                          const origin = stepAt(lane.jump.from);
                          const label = jumpLabel(lane.jump, steps, objectiveAt);
                          const rank = origin === undefined ? '?' : `${origin.index + 1}`;
                          return (
                            <JumpLane
                              key={jumpKey(lane.jump)}
                              lane={lane}
                              incoming={true}
                              showLabel={labels}
                              label={`depuis ${rank} · ${label}`}
                              name={`Saut depuis ${origin === undefined ? 'une étape disparue' : stepTitle(origin)} · ${label}`}
                              onOpen={() => openJump(lane.jump.from)}
                              onPoint={() => setPointed(jumpKey(lane.jump))}
                              onLeave={() => setPointed(null)}
                            />
                          );
                        })
                      : null}
                  </svg>
                  {steps.map((step, index) => (
                    <Fragment key={step.id}>
                      {readOnly ? (
                        index === 0 ? null : (
                          <Connector />
                        )
                      ) : (
                        <>
                          {insertSlot(index)}
                          <Connector />
                        </>
                      )}
                      <StepCard
                        step={step}
                        index={index}
                        selected={step.id === selectedStepId}
                        readOnly={readOnly}
                        problems={problems.get(step.id) ?? 0}
                        dragging={dragged === step.id}
                        onSelect={onSelect}
                        onRemoveEmpty={onRemoveEmpty}
                        onDragStart={setDragged}
                        onDragEnd={() => {
                          setDragged(null);
                          setDropSlot(null);
                        }}
                        registerNode={registerNode}
                      />
                      {readOnly ? null : <Connector />}
                    </Fragment>
                  ))}
                  {readOnly ? null : insertSlot(steps.length)}
                  {links && pointedLane !== null ? (
                    <JumpPopover
                      lane={pointedLane.lane}
                      steps={steps}
                      stepAt={stepAt}
                      objectiveAt={objectiveAt}
                      incoming={pointedLane.incoming}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <ul className="mt-2 flex flex-wrap gap-4 text-gray-600 text-xs">
            <li className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="inline-block w-4 border-bleu-nuit border-t-2" />
              part de l’étape sélectionnée, la condition au départ du couloir
            </li>
            <li className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="inline-block w-4 border-gray-500 border-t-2 border-dashed"
              />
              arrive à l’étape sélectionnée
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Repeat aria-hidden="true" className="size-3.5" />
              essais
            </li>
            <li className="inline-flex items-center gap-1.5">
              <CircleX aria-hidden="true" className="size-3.5" />
              sortie « fermer l’expérience »
            </li>
            <li className="inline-flex items-center gap-1.5">
              <TriangleAlert aria-hidden="true" className="size-3.5" />
              l’enregistrement d’une version refusera cette étape
            </li>
          </ul>
        </>
      )}
    </section>
  );
}
